import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../../contexts/LanguageContext';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import eventEmitter, { EVENTS } from '../../utils/eventEmitter';
import { candidatesAPI, handleApiError, kanbanAPI } from '../../utils/api';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  UserGroupIcon,
  ChartBarIcon,
  SparklesIcon,
  EyeIcon,
  DocumentTextIcon,
  CalendarIcon,
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import CandidateModal from '../../components/CandidateModal';
import ColumnModal from '../../components/ColumnModal';
import ConfirmationModal from '../../components/modals/ConfirmationModal';
import useConfirm from '../../hooks/useConfirm';
import { format } from 'date-fns';
import { fr, enUS, es } from 'date-fns/locale';

const KANBAN_TOAST_IDS = {
  loadBoardError: 'kanban-load-board-error',
  moveCandidateError: 'kanban-move-candidate-error',
  deleteCandidateError: 'kanban-delete-candidate-error',
  deleteColumnError: 'kanban-delete-column-error'
};

const StrictModeDroppable = ({ children, ...props }) => {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    const animationFrame = requestAnimationFrame(() => setEnabled(true));
    return () => {
      cancelAnimationFrame(animationFrame);
      setEnabled(false);
    };
  }, []);

  if (!enabled) return null;
  return <Droppable {...props}>{children}</Droppable>;
};

function KanbanPage() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const { confirm, confirmProps } = useConfirm();
  const [board, setBoard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [candidateModalOpen, setCandidateModalOpen] = useState(false);
  const [columnModalOpen, setColumnModalOpen] = useState(false);
  const [selectedColumn, setSelectedColumn] = useState(null);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [editingColumn, setEditingColumn] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterScore, setFilterScore] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [reorderingBoard, setReorderingBoard] = useState(false);
  const [movingCandidateId, setMovingCandidateId] = useState(null);
  const scrollContainerRef = useRef(null);
  const [showScrollButtons, setShowScrollButtons] = useState(false);
  
  const getLocale = () => {
    const lang = localStorage.getItem('language') || 'fr';
    switch(lang) {
      case 'en': return enUS;
      case 'es': return es;
      default: return fr;
    }
  };

  const showErrorToast = useCallback((message, toastId) => {
    if (toast.isActive(toastId)) return;
    toast.error(message, { toastId });
  }, []);

  // Charger le tableau Kanban
  const fetchBoard = useCallback(async () => {
    try {
      // Fetching kanban board
      const response = await kanbanAPI.getBoard(undefined, { suppressErrorToast: true });
      setBoard(response.data);
    } catch (error) {
      // Kanban error
      showErrorToast(
        handleApiError(error, t('dashboard.kanban.errors.loadingBoardToast')),
        KANBAN_TOAST_IDS.loadBoardError
      );
    } finally {
      setLoading(false);
    }
  }, [showErrorToast, t]);

  useEffect(() => {
    fetchBoard();
    
    // S'abonner aux événements de mise à jour
    const unsubscribeKanbanRefresh = eventEmitter.on(EVENTS.KANBAN_REFRESH_NEEDED, () => {
      console.log('🔄 Kanban refresh requested');
      fetchBoard();
    });
    
    const unsubscribeCandidateDeleted = eventEmitter.on(EVENTS.CANDIDATE_DELETED, () => {
      console.log('🗑️ Candidate deleted, refreshing kanban');
      fetchBoard();
    });
    
    const unsubscribeCvDeleted = eventEmitter.on(EVENTS.CV_DELETED, (data) => {
      if (data.hasKanbanCard) {
        console.log('📋 CV with kanban card deleted, refreshing kanban after delay');
        // Attendre un peu pour s'assurer que la suppression côté serveur est terminée
        setTimeout(() => {
          console.log('🔄 Refreshing kanban now...');
          fetchBoard();
        }, 500);
      }
    });
    
    const unsubscribeCandidateCreated = eventEmitter.on(EVENTS.CANDIDATE_CREATED, () => {
      console.log('✨ Candidate created, might need kanban refresh');
      // Optionnel : on peut décider de ne pas rafraîchir ici car les nouvelles fiches
      // ne sont pas automatiquement partagées au kanban
    });
    
    const unsubscribeCandidateShared = eventEmitter.on(EVENTS.CANDIDATE_SHARED_TO_KANBAN, () => {
      console.log('📤 Candidate shared to kanban, refreshing');
      fetchBoard();
    });
    
    const unsubscribeCandidateUnshared = eventEmitter.on(EVENTS.CANDIDATE_UNSHARED_FROM_KANBAN, () => {
      console.log('📥 Candidate unshared from kanban, refreshing');
      fetchBoard();
    });
    
    // Cleanup des listeners au démontage
    return () => {
      unsubscribeKanbanRefresh();
      unsubscribeCandidateDeleted();
      unsubscribeCvDeleted();
      unsubscribeCandidateCreated();
      unsubscribeCandidateShared();
      unsubscribeCandidateUnshared();
    };
  }, [fetchBoard]);

  // Gérer le drag & drop
  const handleDragEnd = async (result) => {
    if (reorderingBoard) return;
    if (!result.destination) return;

    const { source, destination } = result;
    
    // Si la carte reste dans la même position
    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    ) {
      return;
    }

    // Créer une copie du board pour la mise à jour optimiste
    const newBoard = [...board];
    const sourceColumn = newBoard.find(col => col.id.toString() === source.droppableId);
    const destColumn = newBoard.find(col => col.id.toString() === destination.droppableId);
    
    if (!sourceColumn || !destColumn) return;

    const sourceVisibleCandidates = getFilteredCandidates(sourceColumn.candidates);
    const candidate = sourceVisibleCandidates[source.index];
    if (!candidate) return;

    // Retirer le candidat de la colonne source
    sourceColumn.candidates = sourceColumn.candidates.filter(c => c.id !== candidate.id);
    
    if (sourceColumn.id === destColumn.id) {
      // Reorder inside the same column while preserving hidden cards when filters are active.
      const destinationVisibleCandidates = getFilteredCandidates(sourceColumn.candidates);
      const destinationCandidate = destinationVisibleCandidates[destination.index];

      if (destinationCandidate) {
        const insertIndex = sourceColumn.candidates.findIndex(c => c.id === destinationCandidate.id);
        sourceColumn.candidates.splice(insertIndex, 0, candidate);
      } else {
        sourceColumn.candidates.push(candidate);
      }
    } else {
      // Move between columns while preserving hidden cards when filters are active.
      const destinationVisibleCandidates = getFilteredCandidates(destColumn.candidates);
      const destinationCandidate = destinationVisibleCandidates[destination.index];

      if (destinationCandidate) {
        const insertIndex = destColumn.candidates.findIndex(c => c.id === destinationCandidate.id);
        destColumn.candidates.splice(insertIndex, 0, candidate);
      } else {
        destColumn.candidates.push(candidate);
      }
    }
    
    // Mise à jour optimiste
    setBoard(newBoard);

    try {
      setReorderingBoard(true);
      // Persist with candidate move endpoint (single-card drag), which is
      // less brittle than reconstructing full-column payloads under filters.
      const targetPosition = Math.max(
        0,
        destColumn.candidates.findIndex(candidateItem => candidateItem.id === candidate.id)
      );

      await candidatesAPI.move(
        candidate.id,
        {
          column_id: destColumn.id,
          position: targetPosition
        },
        { suppressErrorToast: true }
      );

      await fetchBoard();
      // Candidate moved successfully
      toast.success(t('dashboard.kanban.messages.candidateMoved'));
    } catch (error) {
      // Failed to move candidate
      showErrorToast(
        handleApiError(error, t('dashboard.kanban.errors.movingCandidateToast')),
        KANBAN_TOAST_IDS.moveCandidateError
      );
      // Recharger le board en cas d'erreur
      fetchBoard();
    } finally {
      setReorderingBoard(false);
    }
  };

  const handleMoveCandidateToColumn = async (candidate, fromColumnId, toColumnId) => {
    if (!candidate?.id) return;
    if (fromColumnId === toColumnId) return;

    const destinationColumn = board.find(col => col.id === toColumnId);
    const targetPosition = destinationColumn?.candidates?.length || 0;

    try {
      setMovingCandidateId(candidate.id);
      await candidatesAPI.move(
        candidate.id,
        { column_id: toColumnId, position: targetPosition },
        { suppressErrorToast: true }
      );
      toast.success(t('dashboard.kanban.messages.candidateMoved'));
      await fetchBoard();
    } catch (error) {
      showErrorToast(
        handleApiError(error, t('dashboard.kanban.errors.movingCandidateToast')),
        KANBAN_TOAST_IDS.moveCandidateError
      );
    } finally {
      setMovingCandidateId(null);
    }
  };

  // Gérer l'ajout/édition de candidat
  const handleEditCandidate = (candidate, column) => {
    setSelectedCandidate(candidate);
    setSelectedColumn(column);
    setCandidateModalOpen(true);
  };

  // Gérer la suppression de candidat
  const handleDeleteCandidate = async (candidateId) => {
    const confirmed = await confirm({
      title: t('dashboard.kanban.confirmations.deleteCandidate.title'),
      message: t('dashboard.kanban.confirmations.deleteCandidate.message'),
      confirmText: t('common.delete'),
      confirmStyle: 'danger'
    });

    if (!confirmed) return;

    try {
      // Deleting candidate
      await candidatesAPI.delete(candidateId, { suppressErrorToast: true });
      
      // Candidate deleted successfully
      toast.success(t('dashboard.kanban.messages.candidateDeleted'));
      fetchBoard();
    } catch (error) {
      // Error
      showErrorToast(
        handleApiError(error, t('dashboard.kanban.errors.deletingCandidateToast')),
        KANBAN_TOAST_IDS.deleteCandidateError
      );
    }
  };

  // Gérer la création de colonne
  const handleCreateColumn = () => {
    setEditingColumn(null);
    setColumnModalOpen(true);
  };

  // Gérer l'édition de colonne
  const handleEditColumn = (column) => {
    setEditingColumn(column);
    setColumnModalOpen(true);
  };

  // Gérer la suppression de colonne
  const handleDeleteColumn = async (columnId) => {
    const column = board.find(col => col.id === columnId);
    
    if (column.candidates && column.candidates.length > 0) {
      const message = t('dashboard.kanban.errors.columnHasCandidates')
        .replace('{{count}}', column.candidates.length.toString());
      toast.warning(message);
      return;
    }

    const confirmed = await confirm({
      title: t('dashboard.kanban.confirmations.deleteColumn.title'),
      message: t('dashboard.kanban.confirmations.deleteColumn.message'),
      confirmText: t('common.delete'),
      confirmStyle: 'danger'
    });

    if (!confirmed) return;

    try {
      // Deleting column
      await kanbanAPI.deleteColumn(columnId, { suppressErrorToast: true });
      
      // Column deleted successfully
      toast.success(t('dashboard.kanban.messages.columnDeleted'));
      fetchBoard();
    } catch (error) {
      // Error
      showErrorToast(
        handleApiError(error, t('dashboard.kanban.errors.deletingColumnToast')),
        KANBAN_TOAST_IDS.deleteColumnError
      );
    }
  };

  // Vérifier si les boutons de scroll doivent être affichés
  useEffect(() => {
    const checkScroll = () => {
      if (scrollContainerRef.current) {
        const { scrollWidth, clientWidth } = scrollContainerRef.current;
        setShowScrollButtons(scrollWidth > clientWidth);
      }
    };

    checkScroll();
    window.addEventListener('resize', checkScroll);
    return () => window.removeEventListener('resize', checkScroll);
  }, [board]);

  // Filtrer les candidats en fonction de la recherche et du score
  const getFilteredCandidates = (candidates) => {
    if (!candidates || !Array.isArray(candidates)) return [];
    return candidates.filter(candidate => {
      const matchesSearch = searchTerm === '' || 
        `${candidate.first_name} ${candidate.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (candidate.title && candidate.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (candidate.specialty && candidate.specialty.toLowerCase().includes(searchTerm.toLowerCase()));
      
      const matchesScore = filterScore === null || 
        (candidate.score && candidate.score >= filterScore);
      
      return matchesSearch && matchesScore;
    });
  };

  // Gérer le scroll horizontal avec les boutons
  const handleScroll = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = 340; // Largeur d'une colonne + gap
      const currentScroll = scrollContainerRef.current.scrollLeft;
      const newScroll = direction === 'left' 
        ? Math.max(0, currentScroll - scrollAmount)
        : currentScroll + scrollAmount;
      
      scrollContainerRef.current.scrollTo({
        left: newScroll,
        behavior: 'smooth'
      });
    }
  };

  // Gérer le mode plein écran
  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const totalCandidates = useMemo(
    () => board.reduce((total, col) => total + (col.candidates?.length || 0), 0),
    [board]
  );
  const visibleCandidates = useMemo(
    () => board.reduce((total, col) => {
      const candidates = Array.isArray(col.candidates) ? col.candidates : [];
      const visibleInColumn = candidates.filter(candidate => {
        const matchesSearch = searchTerm === '' ||
          `${candidate.first_name} ${candidate.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (candidate.title && candidate.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (candidate.specialty && candidate.specialty.toLowerCase().includes(searchTerm.toLowerCase()));
        const matchesScore = filterScore === null || (candidate.score && candidate.score >= filterScore);
        return matchesSearch && matchesScore;
      }).length;

      return total + visibleInColumn;
    }, 0),
    [board, searchTerm, filterScore]
  );
  const hasActiveFilters = Boolean(searchTerm || filterScore !== null);
  const getScoreClasses = (score) => {
    if (score >= 90) return 'bg-emerald-100 text-emerald-800 border-emerald-200';
    if (score >= 80) return 'bg-blue-100 text-blue-800 border-blue-200';
    if (score >= 70) return 'bg-amber-100 text-amber-800 border-amber-200';
    return 'bg-slate-100 text-slate-700 border-slate-200';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="h-full w-full flex flex-col overflow-hidden">
      <motion.div 
        className={`h-full w-full flex flex-col ${isFullscreen ? 'fixed inset-0 z-50' : ''}`}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        {/* Container principal avec isolation stricte */}
        <div className="h-full w-full flex flex-col bg-gray-50 overflow-hidden relative">
          {/* Zone de contenu avec padding et isolation */}
          <div className="flex-1 flex flex-col overflow-hidden p-4 sm:px-6 lg:px-8 relative z-0">
            {/* Header */}
            <div className="flex-shrink-0 mb-6">
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg">
                        <ChartBarIcon className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <h1 className="text-2xl font-bold text-gray-900">
                          {t('dashboard.kanban.title')}
                        </h1>
                        <p className="text-gray-600 mt-0.5">
                          {t('dashboard.kanban.subtitle')}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Statistiques et actions */}
                  <div className="flex flex-wrap items-center gap-3">
                    {/* Statistiques améliorées */}
                    <div className="flex items-center gap-2">
                      <motion.div 
                        className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg px-4 py-2.5 shadow-sm border border-blue-200"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <div className="flex items-center space-x-2">
                          <UserGroupIcon className="h-5 w-5 text-blue-600" />
                          <div>
                            <p className="text-xs text-blue-600 font-medium">{t('dashboard.kanban.stats.totalCandidates')}</p>
                            <p className="text-lg font-bold text-blue-900">{totalCandidates}</p>
                          </div>
                        </div>
                      </motion.div>
                      
                      <motion.div 
                        className="bg-gradient-to-r from-green-50 to-green-100 rounded-lg px-4 py-2.5 shadow-sm border border-green-200"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <div className="flex items-center space-x-2">
                          <SparklesIcon className="h-5 w-5 text-green-600" />
                          <div>
                            <p className="text-xs text-green-600 font-medium">{t('dashboard.kanban.stats.activeColumns')}</p>
                            <p className="text-lg font-bold text-green-900">{board.length}</p>
                          </div>
                        </div>
                      </motion.div>

                      <motion.div
                        className="bg-gradient-to-r from-violet-50 to-violet-100 rounded-lg px-4 py-2.5 shadow-sm border border-violet-200"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <div className="flex items-center space-x-2">
                          <FunnelIcon className="h-5 w-5 text-violet-600" />
                          <div>
                            <p className="text-xs text-violet-600 font-medium">{t('dashboard.kanban.stats.visibleCandidates')}</p>
                            <p className="text-lg font-bold text-violet-900">{visibleCandidates}</p>
                          </div>
                        </div>
                      </motion.div>
                    </div>

                    {/* Boutons d'action */}
                    <div className="flex items-center gap-2">
                      <motion.button
                        onClick={toggleFullscreen}
                        className="p-2.5 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        title={isFullscreen ? t('dashboard.kanban.exitFullscreen') : t('dashboard.kanban.enterFullscreen')}
                        aria-label={isFullscreen ? t('dashboard.kanban.exitFullscreen') : t('dashboard.kanban.enterFullscreen')}
                      >
                        {isFullscreen ? <ArrowsPointingInIcon className="h-5 w-5 text-gray-700" /> : <ArrowsPointingOutIcon className="h-5 w-5 text-gray-700" />}
                      </motion.button>
                      
                      {user?.role === 'admin' && (
                        <motion.button
                          onClick={handleCreateColumn}
                          className="btn-primary flex items-center gap-2"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <PlusIcon className="h-5 w-5" />
                          <span className="hidden sm:inline">{t('dashboard.kanban.actions.newColumn')}</span>
                          <span className="sm:hidden">{t('dashboard.kanban.actions.column')}</span>
                        </motion.button>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Barre de recherche et filtres */}
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <div className="flex flex-col sm:flex-row gap-3">
                    <div className="flex-1 relative">
                      <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input
                        type="text"
                        placeholder={t('dashboard.kanban.searchPlaceholder')}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                        aria-label={t('dashboard.kanban.searchPlaceholder')}
                      />
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <div className="relative">
                        <FunnelIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                        <select
                          value={filterScore || ''}
                          onChange={(e) => setFilterScore(e.target.value ? parseInt(e.target.value) : null)}
                          className="pl-10 pr-8 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent appearance-none cursor-pointer transition-all"
                          aria-label={t('dashboard.kanban.filters.allScores')}
                        >
                          <option value="">{t('dashboard.kanban.filters.allScores')}</option>
                          <option value="70">{t('dashboard.kanban.filters.above70')}</option>
                          <option value="80">{t('dashboard.kanban.filters.above80')}</option>
                          <option value="90">{t('dashboard.kanban.filters.above90')}</option>
                        </select>
                      </div>
                      {hasActiveFilters && (
                        <button
                          type="button"
                          onClick={() => {
                            setSearchTerm('');
                            setFilterScore(null);
                          }}
                          className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100"
                        >
                          {t('common.clearFilters')}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Kanban Board Container */}
            <div className="flex-1 min-h-0 relative" style={{ minHeight: '500px' }}>
              <div className="absolute inset-0 overflow-hidden">
                <div className="h-full relative">
                {/* Boutons de navigation */}
                <AnimatePresence>
                  {showScrollButtons && (
                    <>
                      <motion.button
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        onClick={() => handleScroll('left')}
                        className="absolute left-4 top-1/2 -translate-y-1/2 z-20 p-2.5 bg-white backdrop-blur-sm rounded-full shadow-lg hover:bg-white hover:shadow-xl transition-all"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        aria-label={t('common.previous')}
                      >
                        <ChevronLeftIcon className="h-6 w-6 text-gray-700" />
                      </motion.button>
                      
                      <motion.button
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        onClick={() => handleScroll('right')}
                        className="absolute right-4 top-1/2 -translate-y-1/2 z-20 p-2.5 bg-white backdrop-blur-sm rounded-full shadow-lg hover:bg-white hover:shadow-xl transition-all"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        aria-label={t('common.next')}
                      >
                        <ChevronRightIcon className="h-6 w-6 text-gray-700" />
                      </motion.button>
                    </>
                  )}
                </AnimatePresence>
                
                  {/* Zone de scroll avec conteneur d'isolation */}
                  <div className="h-full relative">
                    <DragDropContext onDragEnd={handleDragEnd}>
                      <div className="absolute inset-0">
                        <div
                          ref={scrollContainerRef}
                          className="h-full overflow-x-auto overflow-y-hidden kanban-scroll-container"
                          style={{ scrollbarGutter: 'stable', touchAction: 'pan-x' }}
                        >
                          <div className="flex h-full min-h-[400px] min-w-max snap-x snap-mandatory gap-5 px-3 pb-4 pr-8">
                        {board.map((column, columnIndex) => {
                          const filteredCandidates = getFilteredCandidates(column.candidates);
                          
                          return (
                            <motion.div 
                              key={column.id} 
                              className="h-full w-[360px] flex-shrink-0 snap-start"
                              initial={{ opacity: 0, x: 50 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: columnIndex * 0.1 }}
                            >
                              <div className="h-full flex flex-col bg-white rounded-lg border border-slate-200 overflow-hidden">
                                {/* Column Header */}
                                <motion.div 
                                  className="relative px-4 py-2.5 border-b border-slate-200 bg-white"
                                  whileHover={{ backgroundColor: '#f1f5f9' }}
                                >
                                  <div
                                    className="absolute inset-y-0 left-0 w-1"
                                    style={{ backgroundColor: column.color }}
                                  />
                                  
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0">
                                      <h3 className="text-sm font-semibold text-slate-900 truncate tracking-tight">
                                        {t(`dashboard.kanban.columns.${column.name}`, column.name)}
                                      </h3>
                                      <p className="text-[11px] text-slate-500 mt-0.5 uppercase tracking-wide">
                                        {filteredCandidates.length} {t('dashboard.kanban.candidates')}
                                        {filteredCandidates.length < (column.candidates?.length || 0) && (
                                          <span className="text-primary-600 font-semibold">
                                            {' '}· {(column.candidates?.length || 0) - filteredCandidates.length} {t('dashboard.kanban.filtered')}
                                          </span>
                                        )}
                                      </p>
                                    </div>
                                    
                                    <div className="flex items-center gap-1">
                                      <motion.span 
                                        className="px-2 py-0.5 rounded-full text-[11px] font-semibold border"
                                        style={{ 
                                          backgroundColor: `${column.color}15`,
                                          color: column.color,
                                          borderColor: `${column.color}40`
                                        }}
                                        whileHover={{ scale: 1.05 }}
                                      >
                                        {filteredCandidates.length}
                                      </motion.span>
                                      
                                      {user?.role === 'admin' && (
                                        <div className="flex items-center ml-1">
                                          <motion.button
                                            onClick={() => handleEditColumn(column)}
                                            className="p-1.5 hover:bg-slate-100 rounded-md transition-colors"
                                            whileHover={{ scale: 1.05 }}
                                            whileTap={{ scale: 0.95 }}
                                            title={t('dashboard.kanban.tooltips.editColumn')}
                                            aria-label={t('dashboard.kanban.tooltips.editColumn')}
                                          >
                                            <PencilIcon className="h-4 w-4 text-slate-500" />
                                          </motion.button>
                                          <motion.button
                                            onClick={() => handleDeleteColumn(column.id)}
                                            className="p-1.5 hover:bg-red-50 rounded-md transition-colors ml-1"
                                            whileHover={{ scale: 1.05 }}
                                            whileTap={{ scale: 0.95 }}
                                            title={t('dashboard.kanban.tooltips.deleteColumn')}
                                            aria-label={t('dashboard.kanban.tooltips.deleteColumn')}
                                          >
                                            <TrashIcon className="h-4 w-4 text-red-500" />
                                          </motion.button>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </motion.div>

                                {/* Column Body */}
                                <div className="flex-1 min-h-0 relative">
                                  <StrictModeDroppable droppableId={column.id.toString()}>
                                    {(provided, snapshot) => (
                                      <div className="absolute inset-0">
                                        <div
                                          ref={provided.innerRef}
                                          {...provided.droppableProps}
                                          className={`h-full p-4 transition-all duration-200 overflow-y-auto custom-scrollbar ${
                                            snapshot.isDraggingOver 
                                              ? 'bg-slate-100' 
                                              : 'bg-slate-50'
                                          }`}
                                          style={{ 
                                            minHeight: '400px'
                                          }}
                                        >
                                        <AnimatePresence>
                                          {snapshot.isDraggingOver && (
                                            <motion.div 
                                              className="absolute inset-0 pointer-events-none"
                                              initial={{ opacity: 0 }}
                                              animate={{ opacity: 1 }}
                                              exit={{ opacity: 0 }}
                                            >
                                              <div className="h-full w-full border-2 border-dashed border-primary-300 rounded-lg" />
                                            </motion.div>
                                          )}
                                        </AnimatePresence>
                                        
                                        {filteredCandidates.length === 0 ? (
                                          <motion.div 
                                            className="flex flex-col items-center justify-center h-48 text-gray-400"
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                          >
                                            <UserGroupIcon className="h-12 w-12 mb-3 text-gray-300" />
                                            <p className="text-sm font-medium text-gray-500">
                                              {(column.candidates?.length || 0) === 0 
                                                ? t('dashboard.kanban.noCandidates')
                                                : t('dashboard.kanban.noMatchingCandidates')
                                              }
                                            </p>
                                            {(column.candidates?.length || 0) > 0 && (
                                              <p className="text-xs text-gray-400 mt-1">
                                                {t('dashboard.kanban.tryAdjustingFilters')}
                                              </p>
                                            )}
                                          </motion.div>
                                        ) : (
                                          <div className="space-y-2 pb-4">
                                            {filteredCandidates.map((candidate, index) => {
                                              return (
                                                <Draggable
                                                  key={candidate.id}
                                                  draggableId={candidate.id.toString()}
                                                  index={index}
                                                  isDragDisabled={reorderingBoard || movingCandidateId === candidate.id}
                                                >
                                                  {(provided, snapshot) => (
                                                    <div
                                                      ref={provided.innerRef}
                                                      {...provided.draggableProps}
                                                      {...provided.dragHandleProps}
                                                    >
                                                      <motion.div
                                                        layout
                                                        initial={{ opacity: 0, scale: 0.98 }}
                                                        animate={{ opacity: 1, scale: 1 }}
                                                        exit={{ opacity: 0, scale: 0.98 }}
                                                        whileHover={{ y: -1 }}
                                                        whileTap={{ scale: 0.98 }}
                                                        transition={{ type: "spring", stiffness: 240, damping: 26 }}
                                                        className={`relative bg-white rounded-md border-l-4 shadow-sm hover:shadow-md transition-all duration-150 cursor-pointer group overflow-hidden ${
                                                          snapshot.isDragging 
                                                            ? 'shadow-lg ring-2 ring-primary-400 border-primary-300' 
                                                            : 'border-slate-200 hover:border-slate-300'
                                                        }`}
                                                        style={{ borderLeftColor: column.color }}
                                                        onClick={() => handleEditCandidate(candidate, column)}
                                                      >
                                                        <div className="p-3">
                                                          <div className="flex items-start gap-2">
                                                            {candidate.photo_url ? (
                                                              <motion.img
                                                                src={candidate.photo_url}
                                                                alt={`${candidate.first_name} ${candidate.last_name}`}
                                                                className="h-9 w-9 rounded-md object-cover ring-1 ring-slate-200"
                                                                whileHover={{ scale: 1.05 }}
                                                              />
                                                            ) : (
                                                              <motion.div 
                                                                className="h-9 w-9 rounded-md bg-primary-600 flex items-center justify-center"
                                                                whileHover={{ scale: 1.05 }}
                                                              >
                                                                <UserGroupIcon className="h-5 w-5 text-white" />
                                                              </motion.div>
                                                            )}
                                                            <div className="min-w-0 flex-1">
                                                              <div className="flex items-start justify-between gap-2">
                                                                <div className="min-w-0">
                                                                  <h4 className="text-sm font-semibold text-slate-900 truncate">
                                                                    {candidate.first_name} {candidate.last_name}
                                                                  </h4>
                                                                  <p className="text-[11px] text-slate-500 truncate mt-0.5">
                                                                    {candidate.title || candidate.specialty || t('dashboard.kanban.candidate')}
                                                                  </p>
                                                                </div>
                                                                {candidate.score && (
                                                                  <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${getScoreClasses(candidate.score)}`}>
                                                                    <StarIconSolid className="h-3 w-3" />
                                                                    {candidate.score}
                                                                  </span>
                                                                )}
                                                              </div>

                                                              <div className="mt-2 space-y-1 text-[11px] text-slate-500">
                                                                {candidate.email && (
                                                                  <div className="flex items-center gap-1.5">
                                                                    <DocumentTextIcon className="h-3.5 w-3.5" />
                                                                    <span className="truncate">{candidate.email}</span>
                                                                  </div>
                                                                )}
                                                                {candidate.created_at && (
                                                                  <div className="flex items-center gap-1.5">
                                                                    <CalendarIcon className="h-3.5 w-3.5" />
                                                                    <span>{format(new Date(candidate.created_at), 'dd MMM yyyy', { locale: getLocale() })}</span>
                                                                  </div>
                                                                )}
                                                              </div>

                                                              <div className="mt-2 flex items-center justify-between">
                                                                <div className="flex flex-wrap gap-1">
                                                                  {candidate.cv_filename && (
                                                                    <span className="inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-blue-700">
                                                                      <DocumentTextIcon className="h-3.5 w-3.5 mr-1" />
                                                                      CV
                                                                    </span>
                                                                  )}
                                                                  {candidate.analyzed && (
                                                                    <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
                                                                      <ChartBarIcon className="h-3.5 w-3.5 mr-1" />
                                                                      {t('dashboard.kanban.analyzed')}
                                                                    </span>
                                                                  )}
                                                                </div>

                                                                <div className="flex items-center gap-1">
                                                                <select
                                                                  value={column.id}
                                                                  onClick={(event) => event.stopPropagation()}
                                                                  onChange={(event) => {
                                                                    event.stopPropagation();
                                                                    handleMoveCandidateToColumn(candidate, column.id, Number(event.target.value));
                                                                  }}
                                                                  disabled={movingCandidateId === candidate.id || reorderingBoard}
                                                                  className="max-w-[110px] rounded-md border border-slate-300 bg-white px-1.5 py-1 text-[11px] font-medium text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100 disabled:opacity-60"
                                                                  aria-label={t('dashboard.kanban.candidateActions.moveToColumn')}
                                                                >
                                                                  {board.map((targetColumn) => (
                                                                    <option key={targetColumn.id} value={targetColumn.id}>
                                                                      {t(`dashboard.kanban.columns.${targetColumn.name}`, targetColumn.name)}
                                                                    </option>
                                                                  ))}
                                                                </select>
                                                                <motion.button
                                                                  onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleEditCandidate(candidate, column);
                                                                  }}
                                                                  className="rounded-md border border-slate-200 bg-white p-1.5 text-slate-600 transition hover:bg-slate-100"
                                                                  whileHover={{ scale: 1.05 }}
                                                                  whileTap={{ scale: 0.95 }}
                                                                  aria-label={t('dashboard.kanban.candidateActions.viewProfile')}
                                                                >
                                                                  <EyeIcon className="h-4 w-4 text-slate-600" />
                                                                </motion.button>
                                                                <motion.button
                                                                  onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleDeleteCandidate(candidate.id);
                                                                  }}
                                                                  className="rounded-md border border-red-200 bg-red-50 p-1.5 text-red-600 transition hover:bg-red-100"
                                                                  whileHover={{ scale: 1.05 }}
                                                                  whileTap={{ scale: 0.95 }}
                                                                  aria-label={t('dashboard.kanban.candidateActions.delete')}
                                                                >
                                                                  <TrashIcon className="h-4 w-4 text-red-500" />
                                                                </motion.button>
                                                                </div>
                                                              </div>
                                                            </div>
                                                          </div>
                                                        </div>
                                                      </motion.div>
                                                    </div>
                                                  )}
                                                </Draggable>
                                              );
                                            })}
                                          </div>
                                        )}
                                          {provided.placeholder}
                                        </div>
                                      </div>
                                    )}
                                  </StrictModeDroppable>
                                </div>
                              </div>
                            </motion.div>
                          );
                        })}
                          </div>
                        </div>
                      </div>
                    </DragDropContext>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Modals */}
      {candidateModalOpen && (
        <CandidateModal
          isOpen={candidateModalOpen}
          onClose={() => setCandidateModalOpen(false)}
          onSuccess={fetchBoard}
          candidate={selectedCandidate}
          columnId={selectedColumn?.id}
        />
      )}

      {columnModalOpen && user?.role === 'admin' && (
        <ColumnModal
          isOpen={columnModalOpen}
          onClose={() => setColumnModalOpen(false)}
          onSuccess={fetchBoard}
          column={editingColumn}
        />
      )}

      <ConfirmationModal {...confirmProps} />
    </div>
  );
}

export default KanbanPage;
